// The limiter of the single-map reads (Task 14, R48; API 0.4 as corrected). The API counts the 2xx `GET /projects/{id}/maps/{name}` and nothing else, and
// answers a read it will not serve with 429: `map_read_burst` (too many in a short time: ask again after `retryAfter` seconds) or `map_read_daily_cap`
// (no more reads today: `retryAfter` is the seconds to midnight UTC). Every single-map read goes through `mapReadLimiter.run`.
import {ApiError} from 'repository/errors';

const defaultSleep = ms => new Promise(resolve => setTimeout(resolve, ms));

const BURST_DEFAULT_SECONDS = 30;
const BURST_MAX_SECONDS = 60;

// The one set of tests for the API's read limits, used wherever a 429 is told apart: any 429, a burst, or the daily cap.
export const isReadLimited = error => error instanceof ApiError && error.status === 429;
const isCode = (error, code) => isReadLimited(error) && error.code === code;
export const isBurst = error => isCode(error, 'map_read_burst');
export const isDailyCap = error => isCode(error, 'map_read_daily_cap');

// The seconds from `at` to the next midnight UTC, for a daily-cap answer that names no retryAfter.
const secondsToMidnightUtc = at => Math.ceil((Date.UTC(new Date(at).getUTCFullYear(), new Date(at).getUTCMonth(), new Date(at).getUTCDate() + 1) - at) / 1000);

/**
 * @param {Object} [options]
 * @param {function(number): Promise} [options.sleep] - waits that many milliseconds; the tests give their own
 * @param {function(): number} [options.now] - the clock, in milliseconds
 * @param {function(number): void} [options.onBurst] - told the seconds of a pause, once for each pause
 * @param {function(): void} [options.onDailyCap] - told once for each day that the cap is in force
 * @returns {{run: function(function(): Promise, {wait: boolean}=): Promise, setHandlers: function({onBurst: function, onDailyCap: function}): void, reset: function(): void}}
 *   run(fn, {wait = true}) - calls fn() (one read) under the limits:
 *     - While the daily cap is in force it rejects at once with the ApiError that began it, without calling fn.
 *     - While a burst pause is in force it waits for the pause to end (the reads that are out together share one pause), or with `wait: false` rejects at
 *       once with the ApiError that began it, without calling fn.
 *     - A 429 map_read_burst from fn starts a pause of min(retryAfter ?? 30, 60) seconds (or joins the pause that is on), tells onBurst, waits the pause
 *       out and calls fn() once more; a second 429 is thrown as it is. With `wait: false` the error is thrown at once, with no retry.
 *     - A 429 map_read_daily_cap from fn is kept until its retryAfter has passed, tells onDailyCap, and is thrown.
 *     - Any other failure is thrown as it is.
 *   setHandlers({onBurst, onDailyCap}) - replaces the handlers; a handler that is left out does nothing.
 */
export const createRateLimiter = ({sleep = defaultSleep, now = Date.now, onBurst = () => {}, onDailyCap = () => {}} = {}) => {
    let handlers = {onBurst, onDailyCap};
    // The pause that is in force: {error, promise}, the promise settling when the pause is over. Null when there is none.
    let pause = null;
    // The cap that is in force: {error, until}. Null when there is none, or when the day it named is over.
    let cap = null;

    const setHandlers = ({onBurst: burst = () => {}, onDailyCap: dailyCap = () => {}} = {}) => {
        handlers = {onBurst: burst, onDailyCap: dailyCap};
    };

    const refuseUnderCap = () => {
        if (cap !== null && cap.until > now()) {
            throw cap.error;
        }
    };

    const beginPause = error => {
        const seconds = Math.min(error.retryAfter ?? BURST_DEFAULT_SECONDS, BURST_MAX_SECONDS);
        const current = {error};
        current.promise = Promise.resolve(sleep(seconds * 1000)).then(() => {
            if (pause === current) {
                pause = null;
            }
        });
        pause = current;
        handlers.onBurst(seconds);
        return current;
    };

    const beginCap = error => {
        const fresh = cap === null || cap.until <= now();
        const seconds = Number.isInteger(error.retryAfter) ? error.retryAfter : secondsToMidnightUtc(now());
        // The reads that were out together all come back with the same cap; the first one sets it, and the day's modal is shown once.
        if (fresh) {
            cap = {error, until: now() + seconds * 1000};
            handlers.onDailyCap();
        }
    };

    const run = async (fn, {wait = true} = {}) => {
        refuseUnderCap();
        if (pause !== null) {
            if (!wait) {
                throw pause.error;
            }
            await pause.promise;
            refuseUnderCap();
        }

        try {
            return await fn();
        } catch (error) {
            if (isDailyCap(error)) {
                beginCap(error);
                throw error;
            }
            if (!isBurst(error)) {
                throw error;
            }

            const current = pause ?? beginPause(error);
            if (!wait) {
                throw error;
            }
            await current.promise;
            refuseUnderCap();

            try {
                return await fn();
            } catch (second) {
                if (isDailyCap(second)) {
                    beginCap(second);
                }
                throw second;
            }
        }
    };

    // For the tests: forgets a pause and a cap, so that one test's 429 cannot hold back the next test's reads (the handlers stay).
    const reset = () => {
        pause = null;
        cap = null;
    };

    return {run, setHandlers, reset};
};

// The one limiter of the page: the API's limits are the account's, whichever window or action reads. The provider binds its handlers when it mounts.
export const mapReadLimiter = createRateLimiter();
